import { Box, Button, Text, useToggle } from '@factorialco/deprecated-design-system'
import { AtsFileFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDeleteAtsFile } from 'modules/ats/hooks/files/useDeleteAtsFile'

interface Props {
  cv: AtsFileFragment
  readOnly?: boolean
  jobTitle?: string
}

export const CurriculumItem = ({ cv, readOnly = false, jobTitle }: Props) => {
  const { deleteAtsFile, loading: deleting } = useDeleteAtsFile(cv)
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false)

  const onDelete = async () => {
    const result = await deleteAtsFile()
    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
      toggleDeleteModal(false)
    }

    return handler
  }

  if (!cv) return null

  return (
    <>
      <Box
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{ all: 'abs008' }}
        paddingX="s24"
        paddingY="s16"
      >
        <Box
          flexGrow
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          gap="s16"
        >
          <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
            <DocumentType mimetype={cv.file.contentType} />
            <Box gap="s4">
              <Box overflowX="hidden" maxWidth="s400">
                <Text.Normal weight="medium" ellipsis overflowWrap>
                  {cv.file.filename}
                </Text.Normal>
              </Box>
              <Text.Normal size="200" color="grey600">
                {moment(cv.createdAt).format('ll')}
                {jobTitle ? ` - ${jobTitle}` : null}
              </Text.Normal>
            </Box>
          </Box>

          <Box flexDirection="row" gap="s8">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative
                  type="secondary"
                  aria-label={i18n.t('ats.candidates.candidate_docs.more_actions')}
                  iconName="ellipsis"
                />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end" disablePortal>
                <PopoverMenu.HrefOption
                  href={cv.file.url}
                  target="_blank"
                  title={i18n.t('documents.download')}
                  download
                />
                {!readOnly && (
                  <PopoverMenu.TouchableOption
                    onClick={toggleDeleteModal(true)}
                    title={i18n.t('documents.delete')}
                    danger
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>

            {cv.preview && (
              <Button.Router
                type="secondary"
                aria-label={i18n.t('documents.preview')}
                to={cv.preview.url}
                blankTarget
                iconName="arrow-right"
              />
            )}
          </Box>
        </Box>
      </Box>

      {isDeleteModalOpen && !deleting && (
        <Modal
          title={i18n.t('documents.confirm_delete.title')}
          description={i18n.t('documents.confirm_delete.description')}
          illustration="input-field"
          onClose={toggleDeleteModal(false)}
          onSubmit={onDelete}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('documents.delete')}
            </Button>
          }
        />
      )}
    </>
  )
}
